<template>
  <section class="shipment-summary" aria-label="Shipment status" :aria-busy="loading">
    <article
      v-for="item in statsData"
      :key="item.title"
      class="shipment-stat"
      :class="{ attention: item.title === 'Exception' || item.title === 'Failed Attempt' }"
    >
      <div>
        <h2>{{ item.title }}</h2>
        <i :class="item.icon.replace('float-end', '')" aria-hidden="true"></i>
      </div>
      <strong v-if="loading" class="stat-placeholder"><span class="stat-skeleton" aria-hidden="true"></span><span class="visually-hidden">Loading {{ item.title }}</span></strong>
      <strong v-else-if="error">—</strong>
      <strong v-else>{{ item.number === '' ? '—' : Number(String(item.number).replace(/[\s,]/g, '')).toLocaleString('en-GB') }}</strong>
      <p v-if="loading">Loading…</p>
      <p v-else-if="error">Could not load data</p>
      <p v-else-if="item.subtext">
        <span>{{ item.subtext }}%</span> vs previous period
      </p>
      <p v-else>Selected period</p>
    </article>
  </section>
  <details class="geography card" :open="!demo">
    <summary>
      Shipment geography <span>{{ markers.length }} locations</span><i class="mdi mdi-chevron-down" aria-hidden="true"></i>
    </summary>
    <div v-if="demo || mapError" class="map-message">
      <i class="mdi mdi-map-marker-radius-outline" aria-hidden="true"></i>
      <p>
        {{
          demo
            ? 'The interactive map is available with a connected maps service.'
            : 'The map could not be loaded. Refresh the page to try again.'
        }}
      </p>
      <small>{{ markers.length }} locations in the selected period.</small>
    </div>
    <div v-show="!demo && !mapError" ref="mapHost" class="map-host"></div>
  </details>
</template>
<script lang="ts">
import type { ShipmentStatisticItem } from '@/typings/Shipments/ShipmentStatisticItem'
import type { MarkerItem } from '@/typings/Shipments/MarkerItem'
import type { HeatmapOverlay } from '@/typings/Shipments/HeatmapOverlay'
import { importGoogleMapsLibrary } from '@/plugins/google-maps'
import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  type Ref,
  type ComputedRef,
  type PropType,
} from 'vue'

export default defineComponent({
  props: {
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    markers: { type: Array as PropType<MarkerItem[]>, required: true },
    stats: { type: Array as PropType<ShipmentStatisticItem[]>, required: true },
  },
  setup(props) {
    const demo = import.meta.env.DEV && import.meta.env.VITE_USE_API_MOCKS === 'true'
    const mapError = ref(false)
    const mapHost = ref<HTMLDivElement | null>(null)
    const map: Ref<google.maps.Map | null> = ref(null)
    const overlay: Ref<HeatmapOverlay | null> = ref(null)

    const center = ref<google.maps.LatLngLiteral>({ lat: 63.339025, lng: 18.065818 })
    const zoom: Ref<number> = ref(4)

    const mapOptions: google.maps.MapOptions = {
      streetViewControl: false,
      scrollwheel: true,
      styles: [
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e9e9e9' }, { lightness: 17 }] },
        { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }, { lightness: 20 }] },
        {
          featureType: 'road.highway',
          elementType: 'geometry.fill',
          stylers: [{ color: '#ffffff' }, { lightness: 17 }],
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry.stroke',
          stylers: [{ color: '#ffffff' }, { lightness: 29 }, { weight: 0.2 }],
        },
        { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffffff' }, { lightness: 18 }] },
        { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#ffffff' }, { lightness: 16 }] },
        { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }, { lightness: 21 }] },
        { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#dedede' }, { lightness: 21 }] },
        { elementType: 'labels.text.stroke', stylers: [{ visibility: 'on' }, { color: '#ffffff' }, { lightness: 16 }] },
        { elementType: 'labels.text.fill', stylers: [{ saturation: 36 }, { color: '#333333' }, { lightness: 40 }] },
        { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#f2f2f2' }, { lightness: 19 }] },
        {
          featureType: 'administrative',
          elementType: 'geometry.fill',
          stylers: [{ color: '#fefefe' }, { lightness: 20 }],
        },
        {
          featureType: 'administrative',
          elementType: 'geometry.stroke',
          stylers: [{ color: '#fefefe' }, { lightness: 17 }, { weight: 1.2 }],
        },
      ],
    }

    const statsData: ComputedRef<ShipmentStatisticItem[]> = computed(() => props.stats)

    const createHeatmapOverlay = (initialMarkers: MarkerItem[]): HeatmapOverlay => {
      class CanvasHeatmapOverlay extends google.maps.OverlayView {
        private canvas: HTMLCanvasElement | null = null
        private markerItems: MarkerItem[] = initialMarkers

        onAdd() {
          this.canvas = document.createElement('canvas')
          this.canvas.style.position = 'absolute'
          this.canvas.style.pointerEvents = 'none'
          this.getPanes()?.overlayLayer.appendChild(this.canvas)
        }

        draw() {
          if (!this.canvas) return

          const projection = this.getProjection()
          const currentMap = this.getMap() as google.maps.Map | null
          const bounds = currentMap?.getBounds()
          const div = mapHost.value

          if (!projection || !bounds || !div) return

          const topLeft = projection.fromLatLngToDivPixel(bounds.getNorthEast())
          const bottomRight = projection.fromLatLngToDivPixel(bounds.getSouthWest())

          if (!topLeft || !bottomRight) return

          const width = div.clientWidth
          const height = div.clientHeight
          const canvas = this.canvas
          const pixelRatio = window.devicePixelRatio || 1
          const currentZoom = currentMap?.getZoom() || zoom.value
          const zoomDelta = Math.max(0, currentZoom - 4)
          const zoomRadiusScale = Math.min(2.4, 1 + zoomDelta * 0.16)
          const zoomAlphaScale = Math.min(5.2, Math.pow(1.45, zoomDelta))
          const heatCanvas = document.createElement('canvas')

          canvas.style.left = `${bottomRight.x}px`
          canvas.style.top = `${topLeft.y}px`
          canvas.style.width = `${width}px`
          canvas.style.height = `${height}px`
          canvas.width = width * pixelRatio
          canvas.height = height * pixelRatio
          heatCanvas.width = canvas.width
          heatCanvas.height = canvas.height

          const context = canvas.getContext('2d')
          const heatContext = heatCanvas.getContext('2d')

          if (!context || !heatContext) return

          context.clearRect(0, 0, canvas.width, canvas.height)
          heatContext.clearRect(0, 0, heatCanvas.width, heatCanvas.height)
          heatContext.scale(pixelRatio, pixelRatio)

          for (const marker of this.markerItems) {
            const position = projection.fromLatLngToDivPixel(
              new google.maps.LatLng(parseFloat(marker.position.lat), parseFloat(marker.position.lng)),
            )

            if (!position) continue

            const x = position.x - bottomRight.x
            const y = position.y - topLeft.y
            const radius = 12 * zoomRadiusScale
            const gradient = heatContext.createRadialGradient(x, y, 0, x, y, radius)
            const alpha = Math.min(0.78, (0.04 + Math.sqrt(Math.max(marker.weight, 1)) / 42) * zoomAlphaScale)

            gradient.addColorStop(0, `rgba(0, 0, 0, ${alpha})`)
            gradient.addColorStop(0.35, `rgba(0, 0, 0, ${alpha * 0.38})`)
            gradient.addColorStop(0.72, `rgba(0, 0, 0, ${alpha * 0.12})`)
            gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')

            heatContext.fillStyle = gradient
            heatContext.beginPath()
            heatContext.arc(x, y, radius, 0, Math.PI * 2)
            heatContext.fill()
          }

          const heatImage = heatContext.getImageData(0, 0, heatCanvas.width, heatCanvas.height)
          const pixels = heatImage.data

          for (let index = 0; index < pixels.length; index += 4) {
            const density = pixels[index + 3] / 255

            if (density <= 0) continue

            let color = [255, 255, 178]

            if (density > 0.92) {
              color = [189, 0, 38]
            } else if (density > 0.74) {
              color = [240, 59, 32]
            } else if (density > 0.52) {
              color = [253, 141, 60]
            } else if (density > 0.3) {
              color = [254, 217, 118]
            }

            pixels[index] = color[0]
            pixels[index + 1] = color[1]
            pixels[index + 2] = color[2]
            pixels[index + 3] = Math.min(210, Math.round(density * 230))
          }

          context.putImageData(heatImage, 0, 0)
        }

        onRemove() {
          this.canvas?.remove()
          this.canvas = null
        }

        setMarkers(items: MarkerItem[]) {
          this.markerItems = items
          this.draw()
        }
      }

      return new CanvasHeatmapOverlay()
    }

    onMounted(async () => {
      if (!mapHost.value || demo) return
      try {
        const { Map } = await importGoogleMapsLibrary()

        map.value = new Map(mapHost.value, {
          center: center.value,
          zoom: zoom.value,
          ...mapOptions,
        })

        overlay.value = createHeatmapOverlay(props.markers)
        overlay.value.setMap(map.value)
      } catch {
        mapError.value = true
      }
    })

    watch(
      () => props.markers,
      (next) => {
        if (!overlay.value) return
        overlay.value.setMarkers(next)
      },
      { deep: true },
    )

    onBeforeUnmount(() => {
      overlay.value?.setMap(null)
      overlay.value = null
      map.value = null
    })

    return { mapHost, statsData, demo, mapError }
  },
})
</script>

<style scoped>
.shipment-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 22px;
}
.shipment-stat {
  border: 1px solid #e0e8e2;
  border-radius: 10px;
  padding: 18px 20px;
  background: white;
  min-width: 0;
}
.shipment-stat > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.shipment-stat h2 {
  font-size: 14px;
  font-weight: 400;
  color: #647b6d;
  margin: 0;
}
.shipment-stat i {
  position: static;
  font-size: 21px;
  color: #638776;
}
.shipment-stat strong {
  font-size: 30px;
  color: #224735;
  letter-spacing: -0.7px;
  font-weight: 500;
  display: block;
  margin: 8px 0;
}
.stat-placeholder { height: 1.5em; display: flex !important; align-items: center; }
.stat-skeleton { display: block; width: 96px; max-width: 80%; height: 27px; border-radius: 5px; background: linear-gradient(100deg, #e5eee8 20%, #f6faf7 45%, #e5eee8 70%); background-size: 220% 100%; animation: stat-shimmer 1.6s ease-in-out infinite; }
.attention .stat-skeleton { background-image: linear-gradient(100deg, #f3e7dd 20%, #fffaf5 45%, #f3e7dd 70%); }
@keyframes stat-shimmer { from { background-position: 120% 0; } to { background-position: -120% 0; } }
@media (prefers-reduced-motion: reduce) { .stat-skeleton { animation: none; } }
.shipment-stat p {
  font-size: 12px;
  color: #758679;
  margin: 0;
}
.shipment-stat p span {
  color: #426e53;
}
.attention {
  background: #fffcf9;
  border-color: #ebddd3;
}
.attention strong,
.attention i {
  color: #b66838;
}
.geography summary {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 58px;
  padding: 16px 20px;
  font-size: 16px;
  color: #2c5340;
}
.geography summary span {
  font-size: 12px;
  color: #7d9181;
  margin-left: auto;
}
.geography summary::marker {
  content: '';
}
.geography[open] summary i {
  transform: rotate(180deg);
}
.map-host {
  height: 340px;
  width: 100%;
}
.map-message {
  padding: 30px;
  text-align: center;
  background: #f2f7f3;
  color: #6c8374;
}
.map-message > i {
  font-size: 32px;
}
.map-message p {
  font-size: 14px;
  margin: 12px 0 6px;
}
.map-message small {
  font-size: 12px;
}
@media (max-width: 600px) {
  .shipment-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
  .shipment-stat {
    padding: 16px 14px;
  }
  .shipment-stat h2 {
    font-size: 13px;
  }
  .shipment-stat strong {
    font-size: 27px;
  }
  .shipment-stat p {
    font-size: 11px;
  }
  .geography summary {
    font-size: 14px;
    padding: 14px;
  }
  .map-host {
    height: 280px;
  }
}
</style>
